import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { ToastContainer } from './components/Toast.tsx';
import { PaywallDialog } from './components/PaywallDialog.tsx';
import { Layout } from './components/Layout.tsx';
import { AuthGate } from './components/AuthGate.tsx';
import { VerificationGate } from './components/VerificationGate.tsx';
import { Chat } from './pages/Chat.tsx';
import { Dashboard } from './pages/Dashboard.tsx';
import { Fuel } from './pages/Fuel.tsx';
import { WeekDetail } from './pages/WeekDetail.tsx';
import { MonthDetail } from './pages/MonthDetail.tsx';
import { RideDetail } from './pages/RideDetail.tsx';
import { Blocks } from './pages/Blocks.tsx';
import { BlockDetail } from './pages/BlockDetail.tsx';
import { TrainTab } from './pages/TrainTab.tsx';
import { ProfilePage } from './pages/ProfilePage.tsx';
import { Welcome } from './pages/Welcome.tsx';
import { OnboardingGate } from './components/setup/OnboardingGate.tsx';
import { getToken } from './lib/auth.ts';
import { setOnUnauthorized } from './lib/api.ts';
import { useStore } from './lib/store.ts';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
    mutations: { retry: 0 },
  },
});

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!getToken());

  // Every sign-out path (401, disabled account, Sign out button) ends here:
  // drop cached queries and the persisted chat so the next user starts clean.
  const handleUnauthorized = useCallback(() => {
    queryClient.clear();
    useStore.getState().clearUser();
    setIsAuthenticated(false);
  }, []);

  useEffect(() => {
    setOnUnauthorized(handleUnauthorized);
  }, [handleUnauthorized]);

  // Listen for auth:logout events dispatched by API client
  useEffect(() => {
    globalThis.addEventListener('auth:logout', handleUnauthorized);
    return () => globalThis.removeEventListener('auth:logout', handleUnauthorized);
  }, [handleUnauthorized]);

  if (!isAuthenticated) {
    return <AuthGate onAuth={() => setIsAuthenticated(true)} />;
  }

  return (
    <VerificationGate
      onSignOut={handleUnauthorized}
      onVerified={() => { void queryClient.invalidateQueries(); }}
    >
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <BrowserRouter>
            <ToastContainer />
            <PaywallDialog />
            <Routes>
              <Route path="/welcome" element={<Welcome />} />
              <Route element={<OnboardingGate />}>
                <Route element={<Layout onDisconnect={handleUnauthorized} />}>
                  <Route path="/" element={<Chat />} />
                  <Route path="/fuel" element={<Fuel />} />
                  <Route path="/rides" element={<Dashboard />} />
                  <Route path="/rides/:id" element={<RideDetail />} />
                  <Route path="/weeks/:iso" element={<WeekDetail />} />
                  <Route path="/months/:iso" element={<MonthDetail />} />
                  <Route path="/blocks" element={<Blocks />} />
                  <Route path="/blocks/:id" element={<BlockDetail />} />
                  <Route path="/tools" element={<Navigate to="/rides" replace />} />
                  <Route path="/train" element={<TrainTab />} />
                  <Route path="/profile" element={<ProfilePage />} />
                </Route>
              </Route>
            </Routes>
          </BrowserRouter>
        </ErrorBoundary>
      </QueryClientProvider>
    </VerificationGate>
  );
}
